import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { DSCard, DSIcon } from '../design-system/primitives';
import StatusBadge from '../components/StatusBadge';
import './CreditBalances.css';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

function CreditBalances() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, owes-us, we-owe
  const [sortField, setSortField] = useState('balance'); // name, balance
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getUsers();
      setUsers(response.data || []);
    } catch (err) {
      setError('Failed to load credit balances');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const balances = useMemo(() => {
    return users.map((user) => ({
      ...user,
      balance: parseFloat(user.credit_balance) || 0
    }));
  }, [users]);

  const totals = useMemo(() => {
    const totalWeOwe = balances.reduce((sum, u) => (u.balance > 0 ? sum + u.balance : sum), 0);
    const totalOwedToUs = balances.reduce((sum, u) => (u.balance < 0 ? sum + Math.abs(u.balance) : sum), 0);
    const countWeOwe = balances.filter((u) => u.balance > 0).length;
    const countOwesUs = balances.filter((u) => u.balance < 0).length;

    return {
      totalWeOwe,
      totalOwedToUs,
      netPosition: totalOwedToUs - totalWeOwe,
      countWeOwe,
      countOwesUs
    };
  }, [balances]);

  const filteredBalances = useMemo(() => {
    let result = balances;

    if (filterType === 'owes-us') {
      result = result.filter((u) => u.balance < 0);
    } else if (filterType === 'we-owe') {
      result = result.filter((u) => u.balance > 0);
    }

    result = [...result].sort((a, b) => {
      let aVal, bVal;
      if (sortField === 'name') {
        aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
        bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
      } else {
        aVal = a.balance;
        bVal = b.balance;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [balances, filterType, sortField, sortDirection]);

  const handleFilterChange = (nextFilter) => {
    setFilterType(nextFilter);
    // Owed-to-us balances are negative, so ascending puts the biggest tab first;
    // we-owe balances are positive, so it takes descending to lead with the biggest.
    setSortField('balance');
    setSortDirection(nextFilter === 'we-owe' ? 'desc' : 'asc');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'balance' ? 'asc' : 'asc');
    }
  };

  if (loading) {
    return <p className="balances-loading">Loading credit balances...</p>;
  }

  return (
    <div className="balances-tab">
      {error && <div className="users-alert error">{error}</div>}

      <div className="card balances-summary-card">
        <div className="balances-summary-grid">
          <div className="balances-summary-item">
            <span className="balances-summary-label">Owed to us</span>
            <span className="balances-summary-value owed-to-us">{formatCurrency(totals.totalOwedToUs)}</span>
            <span className="balances-summary-sub">{totals.countOwesUs} member{totals.countOwesUs === 1 ? '' : 's'}</span>
          </div>
          <div className="balances-summary-item">
            <span className="balances-summary-label">We owe out</span>
            <span className="balances-summary-value we-owe">{formatCurrency(totals.totalWeOwe)}</span>
            <span className="balances-summary-sub">{totals.countWeOwe} member{totals.countWeOwe === 1 ? '' : 's'}</span>
          </div>
          <div className="balances-summary-item">
            <span className="balances-summary-label">Net position</span>
            <span className={`balances-summary-value highlight ${totals.netPosition >= 0 ? 'owed-to-us' : 'we-owe'}`}>
              {formatCurrency(Math.abs(totals.netPosition))}
            </span>
            <span className="balances-summary-sub">
              {totals.netPosition >= 0 ? 'net receivable' : 'net liability'}
            </span>
          </div>
        </div>
      </div>

      <div className="balances-filters">
        <div
          className={`balances-filter-chip ${filterType === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          All ({balances.length})
        </div>
        <div
          className={`balances-filter-chip ${filterType === 'owes-us' ? 'active' : ''}`}
          onClick={() => handleFilterChange('owes-us')}
        >
          Owes Us ({totals.countOwesUs})
        </div>
        <div
          className={`balances-filter-chip ${filterType === 'we-owe' ? 'active' : ''}`}
          onClick={() => handleFilterChange('we-owe')}
        >
          We Owe ({totals.countWeOwe})
        </div>
      </div>

      {filteredBalances.length === 0 ? (
        <div className="card">
          <p className="balances-empty">No members match this filter.</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="table-responsive desktop-only">
            <div className="card">
              <table>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Name {sortField === 'name' && (sortDirection === 'asc' ? <DSIcon name="chevronUp" /> : <DSIcon name="chevronDown" />)}
                    </th>
                    <th>Email</th>
                    <th onClick={() => handleSort('balance')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Balance {sortField === 'balance' && (sortDirection === 'asc' ? <DSIcon name="chevronUp" /> : <DSIcon name="chevronDown" />)}
                    </th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBalances.map((user) => (
                    <tr
                      key={user.id}
                      className="balances-row"
                      onClick={() => navigate(`/users/${user.id}`)}
                    >
                      <td><strong>{user.first_name} {user.last_name}</strong></td>
                      <td>{user.email}</td>
                      <td>
                        <strong className={user.balance < 0 ? 'owed-to-us' : user.balance > 0 ? 'we-owe' : ''}>
                          {formatCurrency(user.balance)}
                        </strong>
                      </td>
                      <td>
                        {user.balance < 0 ? (
                          <StatusBadge variant="error">Owes club</StatusBadge>
                        ) : user.balance > 0 ? (
                          <StatusBadge variant="success">Club owes</StatusBadge>
                        ) : (
                          <StatusBadge variant="neutral">Even</StatusBadge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-only">
            {filteredBalances.map((user) => (
              <DSCard
                key={user.id}
                className="balances-card"
                cornerVariant="md"
                onClick={() => navigate(`/users/${user.id}`)}
              >
                <div className="balances-card-identity">
                  <strong>{user.first_name} {user.last_name}</strong>
                  <span className="balances-card-email">{user.email}</span>
                </div>
                <div className="balances-card-balance">
                  <span className={user.balance < 0 ? 'owed-to-us' : user.balance > 0 ? 'we-owe' : ''}>
                    {formatCurrency(user.balance)}
                  </span>
                  {user.balance < 0 ? (
                    <StatusBadge variant="error">Owes club</StatusBadge>
                  ) : user.balance > 0 ? (
                    <StatusBadge variant="success">Club owes</StatusBadge>
                  ) : (
                    <StatusBadge variant="neutral">Even</StatusBadge>
                  )}
                </div>
              </DSCard>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default CreditBalances;
